import { useState, useCallback } from 'react';
import { CheckCircle, XCircle, Printer } from 'lucide-react';

/**
 * Classic SAAQ Inspection Form
 * Reproduces the paper form style: "Fiche d'entretien CAMION" / "REMORQUE"
 * Grid layout with No | Element | C | NC columns per category
 */

const SaaqInspectionClassic = ({
  categories = [],
  itemStatuses = {},
  onItemStatusChange,
  measurements = {},
  onMeasurementChange,
  vehicleType = 'truck',
  vehicleInfo = {},
  inspectorName = '',
  inspectionDate = '',
  odometer = '',
  notes = '',
  onNotesChange,
  disabled = false,
}) => {
  const getStatus = (catCode, itemCode) => itemStatuses[`${catCode}-${itemCode}`] || null;
  const getMeasurement = (catCode, itemCode) => measurements[`${catCode}-${itemCode}`] || '';

  const totalItems = categories.reduce((acc, c) => acc + c.items.length, 0);
  const conformes = Object.values(itemStatuses).filter(s => s === 'C').length;
  const mineurs = Object.values(itemStatuses).filter(s => s === 'Min').length;
  const majeurs = Object.values(itemStatuses).filter(s => s === 'Maj').length;
  const naCount = Object.values(itemStatuses).filter(s => s === 'NA').length;
  const nonConformes = mineurs + majeurs;

  const handleToggle = useCallback((catCode, itemCode) => {
    if (disabled) return;
    const current = itemStatuses[`${catCode}-${itemCode}`];
    if (!current || current === 'NA') {
      onItemStatusChange(catCode, itemCode, 'C');
    } else if (current === 'C') {
      onItemStatusChange(catCode, itemCode, 'Min');
    } else if (current === 'Min') {
      onItemStatusChange(catCode, itemCode, 'Maj');
    } else {
      onItemStatusChange(catCode, itemCode, 'NA');
    }
  }, [disabled, itemStatuses, onItemStatusChange]);

  const title = vehicleType === 'trailer'
    ? 'FICHE D\'ENTRETIEN REMORQUE ET SEMI-REMORQUE'
    : 'FICHE D\'ENTRETIEN CAMION PORTEUR ET TRACTEUR ROUTIER';

  // Group categories into sections matching the PDF layout
  return (
    <div className="saaq-classic-form bg-white text-black print:bg-white" data-testid="saaq-classic-form">
      {/* ===== HEADER ===== */}
      <div className="border-2 border-black mb-0">
        {/* Title Bar - Red */}
        <div style={{ backgroundColor: '#b91c1c', color: 'white', textAlign: 'center', padding: '8px 16px', WebkitPrintColorAdjust: 'exact', printColorAdjust: 'exact' }}>
          <h1 className="text-sm font-bold tracking-wide print:text-xs">{title}</h1>
        </div>

        {/* Vehicle Info Grid */}
        <div className="grid grid-cols-4 border-t-2 border-black text-xs">
          <div className="p-1.5 border-r border-black">
            <span className="text-[10px] text-gray-500 block">Unite #</span>
            <span className="font-bold">{vehicleInfo.unit_number || ''}</span>
          </div>
          <div className="p-1.5 border-r border-black">
            <span className="text-[10px] text-gray-500 block">Marque</span>
            <span className="font-bold">{vehicleInfo.brand || ''}</span>
          </div>
          <div className="p-1.5 border-r border-black">
            <span className="text-[10px] text-gray-500 block">Modele</span>
            <span className="font-bold">{vehicleInfo.model || ''}</span>
          </div>
          <div className="p-1.5">
            <span className="text-[10px] text-gray-500 block">Annee</span>
            <span className="font-bold">{vehicleInfo.year || ''}</span>
          </div>
        </div>
        <div className="grid grid-cols-4 border-t border-black text-xs">
          <div className="p-1.5 border-r border-black">
            <span className="text-[10px] text-gray-500 block">Plaque</span>
            <span className="font-bold">{vehicleInfo.plate_number || ''}</span>
          </div>
          <div className="p-1.5 border-r border-black">
            <span className="text-[10px] text-gray-500 block">Odometre (km)</span>
            <span className="font-bold">{odometer || ''}</span>
          </div>
          <div className="p-1.5 border-r border-black">
            <span className="text-[10px] text-gray-500 block">Date inspection</span>
            <span className="font-bold">{inspectionDate || ''}</span>
          </div>
          <div className="p-1.5">
            <span className="text-[10px] text-gray-500 block">Inspecteur</span>
            <span className="font-bold">{inspectorName || ''}</span>
          </div>
        </div>
        {vehicleInfo.pnbv && (
          <div className="border-t border-black text-xs px-1.5 py-1">
            <span className="text-[10px] text-gray-500">PNBV: </span>
            <span className="font-bold">{vehicleInfo.pnbv?.toLocaleString()} kg</span>
            {vehicleInfo.vin && (
              <>
                <span className="text-[10px] text-gray-500 ml-4">VIN: </span>
                <span className="font-bold">{vehicleInfo.vin}</span>
              </>
            )}
          </div>
        )}
      </div>

      {/* ===== LEGEND ===== */}
      <div className="border-x-2 border-black px-3 py-1 flex items-center gap-4 text-[10px]" style={{ backgroundColor: '#f3f4f6' }}>
        <span className="font-bold">Legende:</span>
        <span className="flex items-center gap-1"><span style={{ display: 'inline-block', width: '14px', height: '14px', backgroundColor: '#16a34a', color: 'white', fontSize: '8px', fontWeight: 'bold', textAlign: 'center', lineHeight: '14px', WebkitPrintColorAdjust: 'exact', printColorAdjust: 'exact' }}>C</span> Conforme</span>
        <span className="flex items-center gap-1"><span style={{ display: 'inline-block', width: '14px', height: '14px', backgroundColor: '#f59e0b', color: 'black', fontSize: '8px', fontWeight: 'bold', textAlign: 'center', lineHeight: '14px', WebkitPrintColorAdjust: 'exact', printColorAdjust: 'exact' }}>m</span> Mineure</span>
        <span className="flex items-center gap-1"><span style={{ display: 'inline-block', width: '14px', height: '14px', backgroundColor: '#dc2626', color: 'white', fontSize: '8px', fontWeight: 'bold', textAlign: 'center', lineHeight: '14px', WebkitPrintColorAdjust: 'exact', printColorAdjust: 'exact' }}>M</span> Majeure</span>
        <span className="flex items-center gap-1"><span style={{ display: 'inline-block', width: '14px', height: '14px', backgroundColor: '#9ca3af', color: 'white', fontSize: '8px', fontWeight: 'bold', textAlign: 'center', lineHeight: '14px', WebkitPrintColorAdjust: 'exact', printColorAdjust: 'exact' }}>-</span> N/A</span>
      </div>

      {/* ===== INSPECTION GRID ===== */}
      <div className="border-2 border-t-0 border-black">
        {categories.map((cat, catIdx) => (
          <div key={cat.code}>
            {/* Category Header */}
            <div
              className={`flex items-center border-t ${catIdx > 0 ? 'border-t-2' : ''} border-black px-2 py-1`}
              style={{ backgroundColor: '#fef08a', WebkitPrintColorAdjust: 'exact', printColorAdjust: 'exact' }}
            >
              <span className="font-bold text-xs text-black flex-1">
                {cat.order}. {cat.name}
              </span>
              <div className="flex items-center" style={{ gap: '3px' }}>
                <span className="w-7 text-center text-[9px] font-bold" style={{ color: '#15803d' }}>C</span>
                <span className="w-7 text-center text-[9px] font-bold" style={{ color: '#b91c1c' }}>NC</span>
                <span className="w-14 text-center text-[9px] font-bold">Mesure</span>
              </div>
            </div>

            {/* Items */}
            {cat.items.map((item, itemIdx) => {
              const status = getStatus(cat.code, item.code);
              const measurement = getMeasurement(cat.code, item.code);
              const isConf = status === 'C';
              const isNC = status === 'Min' || status === 'Maj';
              const rowBg = isNC
                ? 'bg-red-50'
                : itemIdx % 2 === 0
                  ? 'bg-white'
                  : 'bg-gray-50';

              return (
                <div
                  key={item.code}
                  className={`flex items-center border-t border-gray-300 px-2 py-0.5 ${rowBg} hover:bg-blue-50 transition-colors`}
                  data-testid={`classic-item-${cat.code}-${item.code}`}
                >
                  <span className="text-[10px] text-gray-500 font-mono w-7 shrink-0">{item.code}</span>
                  <span className="text-[11px] text-black flex-1 min-w-0 truncate">{item.name}</span>

                  <div className="flex items-center shrink-0" style={{ gap: '3px' }}>
                    {/* C (Conforme) checkbox */}
                    <button
                      onClick={() => !disabled && onItemStatusChange(cat.code, item.code, isConf ? 'NA' : 'C')}
                      disabled={disabled}
                      style={{
                        width: '28px', height: '20px', textAlign: 'center', fontSize: '10px', fontWeight: 'bold',
                        border: '2px solid #333',
                        backgroundColor: isConf ? '#16a34a' : '#ffffff',
                        color: isConf ? '#ffffff' : 'transparent',
                        WebkitPrintColorAdjust: 'exact', printColorAdjust: 'exact'
                      }}
                      data-testid={`classic-c-${cat.code}-${item.code}`}
                    >
                      {isConf ? 'C' : '\u00A0'}
                    </button>

                    {/* NC (Non conforme) checkbox */}
                    <button
                      onClick={() => {
                        if (disabled) return;
                        if (!isNC) onItemStatusChange(cat.code, item.code, 'Min');
                        else if (status === 'Min') onItemStatusChange(cat.code, item.code, 'Maj');
                        else onItemStatusChange(cat.code, item.code, 'NA');
                      }}
                      disabled={disabled}
                      style={{
                        width: '28px', height: '20px', textAlign: 'center', fontSize: '10px', fontWeight: 'bold',
                        border: '2px solid #333',
                        backgroundColor: status === 'Maj' ? '#dc2626' : status === 'Min' ? '#f59e0b' : '#ffffff',
                        color: status === 'Maj' ? '#ffffff' : status === 'Min' ? '#000000' : 'transparent',
                        WebkitPrintColorAdjust: 'exact', printColorAdjust: 'exact'
                      }}
                      title={status === 'Min' ? 'Mineure (cliquer pour Majeure)' : status === 'Maj' ? 'Majeure (cliquer pour N/A)' : 'Non conforme'}
                      data-testid={`classic-nc-${cat.code}-${item.code}`}
                    >
                      {status === 'Min' ? 'm' : status === 'Maj' ? 'M' : '\u00A0'}
                    </button>

                    {/* Measurement field */}
                    <div className="w-14">
                      {item.requires_measurement ? (
                        <input
                          type="number"
                          value={measurement}
                          onChange={e => onMeasurementChange(cat.code, item.code, e.target.value)}
                          disabled={disabled}
                          placeholder={item.measurement_unit || 'mm'}
                          className="w-full rounded-none px-1 py-0 text-[10px] text-center h-5 bg-white focus:outline-none focus:border-blue-500"
                          style={{ border: '2px solid #333' }}
                          data-testid={`classic-measure-${cat.code}-${item.code}`}
                        />
                      ) : (
                        <span className="block w-full h-5" />
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        ))}
      </div>

      {/* ===== SUMMARY BAR ===== */}
      <div className="border-2 border-t-0 border-black px-3 py-2" style={{ backgroundColor: '#f3f4f6' }}>
        <div className="flex items-center justify-between text-xs">
          <div className="flex items-center gap-4">
            <span className="font-bold">Resultats:</span>
            <span className="flex items-center gap-1 font-bold" style={{ color: '#15803d' }}>
              <CheckCircle size={12} /> {conformes} Conforme(s)
            </span>
            {mineurs > 0 && (
              <span className="flex items-center gap-1 font-bold" style={{ color: '#d97706' }}>
                {mineurs} Mineure(s)
              </span>
            )}
            {majeurs > 0 && (
              <span className="flex items-center gap-1 font-bold" style={{ color: '#dc2626' }}>
                <XCircle size={12} /> {majeurs} Majeure(s)
              </span>
            )}
            <span className="text-gray-500">{naCount} N/A</span>
          </div>
          <span className="font-bold">{Object.keys(itemStatuses).length}/{totalItems} verifies</span>
        </div>
      </div>

      {/* ===== REMARKS SECTION ===== */}
      <div className="border-2 border-t-0 border-black">
        <div style={{ backgroundColor: '#fef08a', padding: '4px 8px', borderBottom: '1px solid black' }}>
          <span className="font-bold text-xs">REMARQUES / DEFECTUOSITES</span>
        </div>
        <div className="p-2 min-h-[60px]">
          {/* List defects */}
          {Object.entries(itemStatuses)
            .filter(([, s]) => s === 'Min' || s === 'Maj')
            .map(([key, status]) => {
              const [catCode, itemCode] = key.split('-');
              const cat = categories.find(c => c.code === catCode);
              const item = cat?.items.find(i => i.code === itemCode);
              return (
                <div key={key} className="flex items-center gap-2 text-xs py-0.5 border-b border-gray-200 last:border-0">
                  <span style={{
                    padding: '0 6px', borderRadius: '3px', fontSize: '9px', fontWeight: 'bold',
                    backgroundColor: status === 'Maj' ? '#dc2626' : '#f59e0b',
                    color: status === 'Maj' ? 'white' : 'black',
                    WebkitPrintColorAdjust: 'exact', printColorAdjust: 'exact'
                  }}>
                    {status === 'Maj' ? 'MAJ' : 'MIN'}
                  </span>
                  <span className="font-mono text-gray-500">#{itemCode}</span>
                  <span>{item?.name || itemCode}</span>
                  <span className="text-gray-400">({cat?.name || catCode})</span>
                </div>
              );
            })}
          {nonConformes === 0 && (
            <p className="text-xs text-gray-400 italic">Aucune defectuosite</p>
          )}
        </div>
      </div>

      {/* ===== NOTES LIBRES / OBSERVATIONS ===== */}
      <div className="border-2 border-t-0 border-black">
        <div style={{ backgroundColor: '#fef08a', padding: '4px 8px', borderBottom: '1px solid black' }}>
          <span className="font-bold text-xs">NOTES / OBSERVATIONS</span>
        </div>
        <div className="p-0">
          <textarea
            value={notes}
            onChange={e => onNotesChange && onNotesChange(e.target.value)}
            disabled={disabled || !onNotesChange}
            placeholder="Ajoutez vos observations ou commentaires ici... (appuyez sur Entree pour passer a la ligne suivante)"
            className="w-full text-sm p-2 bg-white text-black focus:outline-none resize-y"
            rows={6}
            style={{ minHeight: '140px', border: 'none', display: 'block' }}
            data-testid="classic-notes-textarea"
          />
        </div>
      </div>

      {/* ===== SIGNATURE LINE ===== */}
      <div className="border-2 border-t-0 border-black grid grid-cols-2">
        <div className="p-2 border-r border-black">
          <span className="text-[10px] text-gray-500 block mb-4">Signature de l'inspecteur:</span>
          <div className="border-b border-black w-full" />
          <span className="text-[10px] text-gray-500 mt-1 block">{inspectorName}</span>
        </div>
        <div className="p-2">
          <span className="text-[10px] text-gray-500 block mb-4">Date:</span>
          <div className="border-b border-black w-full" />
          <span className="text-[10px] text-gray-500 mt-1 block">{inspectionDate}</span>
        </div>
      </div>

      {/* Print button (hidden in print) */}
      <div className="mt-4 flex justify-center print:hidden">
        <button
          onClick={() => {
            // Compact print with CSS multi-column flow (items coulent naturellement entre pages)
            const vType = vehicleType === 'trailer' ? 'REMORQUE ET SEMI-REMORQUE' : 'CAMION PORTEUR ET TRACTEUR ROUTIER';
            const vi = vehicleInfo || {};

            // ===== HEADER (compact, 3 lignes au total) =====
            let headerHtml = '<table width="100%" cellpadding="1" cellspacing="0" border="1" style="border-collapse:collapse;font-family:Arial,sans-serif;font-size:8px;margin-bottom:2px;">';
            headerHtml += '<tr><td colspan="7" bgcolor="#b91c1c" style="color:white;text-align:center;font-weight:bold;font-size:10px;padding:2px;-webkit-print-color-adjust:exact;print-color-adjust:exact;">FICHE D\'ENTRETIEN ' + vType + '</td></tr>';
            headerHtml += '<tr bgcolor="#fffde7" style="-webkit-print-color-adjust:exact;print-color-adjust:exact;">';
            headerHtml += '<td style="padding:1px 3px;"><b>Unite#</b> ' + (vi.unit_number||'') + '</td>';
            headerHtml += '<td style="padding:1px 3px;"><b>Marque</b> ' + (vi.brand||'') + '</td>';
            headerHtml += '<td style="padding:1px 3px;"><b>Modele</b> ' + (vi.model||'') + '</td>';
            headerHtml += '<td style="padding:1px 3px;"><b>Annee</b> ' + (vi.year||'') + '</td>';
            headerHtml += '<td style="padding:1px 3px;"><b>Plaque</b> ' + (vi.plate_number||'') + '</td>';
            headerHtml += '<td style="padding:1px 3px;"><b>Odo</b> ' + (odometer||'') + ' km</td>';
            headerHtml += '<td style="padding:1px 3px;"><b>PNBV</b> ' + (vi.pnbv ? vi.pnbv.toLocaleString()+' kg' : '-') + '</td>';
            headerHtml += '</tr>';
            headerHtml += '<tr>';
            headerHtml += '<td colspan="3" style="padding:1px 3px;"><b>Inspecteur:</b> ' + (inspectorName||'') + '</td>';
            headerHtml += '<td colspan="2" style="padding:1px 3px;"><b>Date:</b> ' + (inspectionDate||'') + '</td>';
            headerHtml += '<td colspan="2" bgcolor="#f0f0f0" style="font-size:7px;padding:1px 3px;-webkit-print-color-adjust:exact;print-color-adjust:exact;"><b>Leg:</b> ';
            headerHtml += '<span style="display:inline-block;width:11px;height:9px;background:#16a34a;color:white;text-align:center;font-weight:bold;font-size:7px;line-height:9px;">C</span>=OK ';
            headerHtml += '<span style="display:inline-block;width:11px;height:9px;background:#f59e0b;color:black;text-align:center;font-weight:bold;font-size:7px;line-height:9px;">m</span>=Min ';
            headerHtml += '<span style="display:inline-block;width:11px;height:9px;background:#dc2626;color:white;text-align:center;font-weight:bold;font-size:7px;line-height:9px;">M</span>=Maj';
            headerHtml += '</td></tr>';
            headerHtml += '</table>';

            // ===== ITEMS: each as a mini-table inside multi-column flow =====
            let defects = [];
            let itemsHtml = '';
            // Column header (shown once at top of flow)
            const colHeader = '<table class="col-hdr" width="100%" cellpadding="0" cellspacing="0" border="1" style="border-collapse:collapse;font-family:Arial,sans-serif;margin-bottom:1px;">'
              + '<tr bgcolor="#e0e0e0">'
              + '<td style="text-align:center;font-weight:bold;font-size:8px;padding:1px 3px;width:24px;">No</td>'
              + '<td style="font-weight:bold;font-size:8px;padding:1px 3px;">Element</td>'
              + '<td style="text-align:center;font-weight:bold;color:#15803d;font-size:8px;padding:1px;width:16px;">C</td>'
              + '<td style="text-align:center;font-weight:bold;color:#b91c1c;font-size:8px;padding:1px;width:16px;">NC</td>'
              + '<td style="text-align:center;font-weight:bold;font-size:8px;padding:1px;width:32px;">Mes.</td>'
              + '</tr></table>';
            itemsHtml += colHeader;

            categories.forEach(cat => {
              itemsHtml += '<div class="cat-hdr" style="background-color:#fef08a;font-weight:bold;padding:2px 4px;font-size:9px;border:1px solid #333;border-top:0;-webkit-print-color-adjust:exact;print-color-adjust:exact;break-inside:avoid;page-break-inside:avoid;">' + cat.order + '. ' + cat.name + '</div>';
              cat.items.forEach((item, idx) => {
                const key = cat.code + '-' + item.code;
                const st = itemStatuses[key] || '';
                const meas = measurements[key] || '';
                const isC = st === 'C';
                const isMin = st === 'Min';
                const isMaj = st === 'Maj';
                const isNC = isMin || isMaj;
                const rowBg = isNC ? '#fef2f2' : (idx % 2 === 0 ? '#ffffff' : '#f9fafb');
                let cCell;
                if (isC) {
                  cCell = '<td align="center" bgcolor="#16a34a" style="color:white;font-weight:bold;font-size:8px;border:1px solid #333;width:16px;height:11px;padding:0;-webkit-print-color-adjust:exact;print-color-adjust:exact;">C</td>';
                } else {
                  cCell = '<td align="center" bgcolor="#ffffff" style="border:1px solid #333;width:16px;height:11px;padding:0;">&nbsp;</td>';
                }
                let ncCell;
                if (isMaj) {
                  ncCell = '<td align="center" bgcolor="#dc2626" style="color:white;font-weight:bold;font-size:8px;border:1px solid #333;width:16px;height:11px;padding:0;-webkit-print-color-adjust:exact;print-color-adjust:exact;">M</td>';
                } else if (isMin) {
                  ncCell = '<td align="center" bgcolor="#f59e0b" style="color:black;font-weight:bold;font-size:8px;border:1px solid #333;width:16px;height:11px;padding:0;-webkit-print-color-adjust:exact;print-color-adjust:exact;">m</td>';
                } else {
                  ncCell = '<td align="center" bgcolor="#ffffff" style="border:1px solid #333;width:16px;height:11px;padding:0;">&nbsp;</td>';
                }
                itemsHtml += '<table class="item-row" width="100%" cellpadding="0" cellspacing="0" border="1" bgcolor="' + rowBg + '" style="border-collapse:collapse;font-family:Arial,sans-serif;break-inside:avoid;page-break-inside:avoid;-webkit-print-color-adjust:exact;print-color-adjust:exact;">'
                  + '<tr bgcolor="' + rowBg + '">'
                  + '<td style="text-align:center;color:#666;font-family:monospace;font-size:7.5px;padding:0 3px;width:24px;">' + item.code + '</td>'
                  + '<td style="font-size:9px;padding:0 4px;line-height:1.15;">' + item.name + '</td>'
                  + cCell + ncCell
                  + '<td style="text-align:center;font-size:9px;padding:0 1px;width:32px;">' + (meas || '') + '</td>'
                  + '</tr></table>';
                if (isNC) defects.push({code: item.code, name: item.name, cat: cat.name, severity: isMaj ? 'MAJ' : 'MIN', color: isMaj ? '#dc2626' : '#f59e0b', textColor: isMaj ? 'white' : 'black'});
              });
            });

            // Wrap items in a multi-column container (items flow naturally across columns AND pages)
            // column-fill: balance equilibre les colonnes sur la derniere page du flux
            const itemsSection = '<div class="items-flow" style="column-count:2;-webkit-column-count:2;column-gap:6px;-webkit-column-gap:6px;column-fill:balance;-webkit-column-fill:balance;">' + itemsHtml + '</div>';

            // ===== FOOTER: SUMMARY + REMARKS + SIGNATURE (full width) =====
            const okCount = Object.values(itemStatuses).filter(s => s === 'C').length;
            const minCount = Object.values(itemStatuses).filter(s => s === 'Min').length;
            const majCount = Object.values(itemStatuses).filter(s => s === 'Maj').length;
            const naC = Object.values(itemStatuses).filter(s => s === 'NA').length;
            const total = categories.reduce((a,c) => a + c.items.length, 0);

            let footerHtml = '<table width="100%" cellpadding="1" cellspacing="0" border="1" style="border-collapse:collapse;font-family:Arial,sans-serif;font-size:8px;margin-top:2px;">';
            footerHtml += '<tr bgcolor="#f0f0f0" style="-webkit-print-color-adjust:exact;print-color-adjust:exact;"><td style="padding:2px 4px;"><b>Resultats:</b> <span style="color:#15803d;font-weight:bold;">' + okCount + ' Conforme(s)</span>';
            if (minCount > 0) footerHtml += ' | <span style="color:#d97706;font-weight:bold;">' + minCount + ' Mineure(s)</span>';
            if (majCount > 0) footerHtml += ' | <span style="color:#dc2626;font-weight:bold;">' + majCount + ' Majeure(s)</span>';
            footerHtml += ' | ' + naC + ' N/A | <b>' + Object.keys(itemStatuses).length + '/' + total + ' verifies</b></td></tr>';
            footerHtml += '<tr><td bgcolor="#fef08a" style="font-weight:bold;padding:1px 4px;font-size:8px;-webkit-print-color-adjust:exact;print-color-adjust:exact;">REMARQUES / DEFECTUOSITES</td></tr>';
            footerHtml += '<tr><td style="padding:2px 4px;font-size:7.5px;">';
            if (defects.length > 0) {
              defects.forEach(d => {
                footerHtml += '<span style="display:inline-block;padding:0 4px;font-size:7px;font-weight:bold;background:' + d.color + ';color:' + d.textColor + ';margin-right:3px;-webkit-print-color-adjust:exact;print-color-adjust:exact;">' + d.severity + '</span> #' + d.code + ' ' + d.name + ' <span style="color:#888;">(' + d.cat + ')</span><br/>';
              });
            } else {
              footerHtml += '<i style="color:#999;">Aucune defectuosite</i>';
            }
            footerHtml += '</td></tr>';
            // Notes / Observations libres
            if (notes && notes.trim()) {
              footerHtml += '<tr><td bgcolor="#fef08a" style="font-weight:bold;padding:2px 4px;font-size:9px;-webkit-print-color-adjust:exact;print-color-adjust:exact;">NOTES / OBSERVATIONS</td></tr>';
              const escapedNotes = notes.replace(/&/g,'&amp;').replace(/</g,'&lt;').replace(/>/g,'&gt;').replace(/\n/g,'<br/>');
              footerHtml += '<tr><td style="padding:6px 10px;font-size:10px;line-height:1.45;white-space:pre-wrap;word-wrap:break-word;word-break:break-word;overflow-wrap:break-word;max-width:100%;">' + escapedNotes + '</td></tr>';
            }
            footerHtml += '<tr><td style="padding:3px 4px;"><table width="100%" cellpadding="0" cellspacing="0"><tr><td style="font-size:7.5px;color:#555;width:55%;">Signature inspecteur: <u>&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;</u> ' + (inspectorName||'') + '</td><td style="font-size:7.5px;color:#555;">Date: <u>&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;</u> ' + (inspectionDate||'') + '</td></tr></table></td></tr>';
            footerHtml += '</table>';

            // ===== ASSEMBLE + PRINT =====
            const css = '@page { size: letter; margin: 6mm; }'
              + ' body { margin: 0; padding: 0; font-family: Arial, sans-serif; -webkit-print-color-adjust: exact; print-color-adjust: exact; }'
              + ' .items-flow { column-count: 2; -webkit-column-count: 2; column-gap: 6px; column-fill: balance; -webkit-column-fill: balance; }'
              + ' .item-row, .cat-hdr, .col-hdr { break-inside: avoid; page-break-inside: avoid; -webkit-column-break-inside: avoid; }'
              + ' table { -webkit-print-color-adjust: exact; print-color-adjust: exact; }';
            const fullHtml = '<!DOCTYPE html><html><head><title>Inspection SAAQ</title><style>' + css + '</style></head><body>' + headerHtml + itemsSection + footerHtml + '</body></html>';
            const win = window.open('', '_blank');
            win.document.write(fullHtml);
            win.document.close();
            setTimeout(() => win.print(), 500);
          }}
          className="print-btn flex items-center gap-2 bg-black text-white px-6 py-2 rounded-lg hover:bg-gray-800 transition-colors text-sm font-medium"
          data-testid="classic-print-btn"
        >
          <Printer size={16} />
          Imprimer cette fiche
        </button>
      </div>
    </div>
  );
};

export default SaaqInspectionClassic;
